import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormHelperText,
	TextField,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { closeDialog } from '@/features/dialog/dialogSlice';
import {
	useGetClientRowQuery,
	useUpdateClientRowMutation,
	useUpdateClientxMutation,
} from '@/features/clients/clientsApi';
import { useGetDealRowQuery, useUpdateDealRowMutation } from '@/features/deals/dealsApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';

const MAX_LENGTH = 300;

export const LossReasonDialog = () => {
	const dispatch = useDispatch();
	const open = useSelector((state) => state.dialog.open?.lossReason);
	const { clientId, dealId, docId, stageId, insightId } = useSelector(
		(state) => state.dialog.data?.lossReason ?? {},
	);

	// Populate cache for KanbanCard with client data and deal data
	useGetClientRowQuery(clientId, {
		skip: !clientId,
	});

	useGetDealRowQuery(dealId, {
		skip: !dealId,
	});

	const email = useSelector((state) => state.msalAccount.account?.username);
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const [updateClientRow] = useUpdateClientRowMutation();
	const [updateClientx] = useUpdateClientxMutation();
	const [updateDealRow] = useUpdateDealRowMutation();

	const [lossReason, setLossReason] = useState('');
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (open) {
			setLossReason('');
			setLoading(false);
		}
	}, [open]);

	const handleClose = () => dispatch(closeDialog('lossReason'));

	const handleConfirm = async () => {
		setLoading(true);
		try {
			const trimmedReason = lossReason.trim();

			if (dealId) {
				await updateDealRow({
					dealId,
					docId,
					properties: {
						dealStage: stageId,
						lossReason: trimmedReason,
						closed: true,
						dateClosed: new Date().getTime(),
						closedBy: userDetails.userId,
					},
				}).unwrap();
			} else if (insightId != null) {
				await updateClientx({
					clientId,
					insight: { CurrentStatusId: 1 },
					firebase: { stage: stageId, lossReason: trimmedReason },
				}).unwrap();
			} else {
				await updateClientRow({
					clientId,
					docId,
					properties: { stage: stageId, lossReason: trimmedReason },
				}).unwrap();
			}

			dispatch(closeDialog('lossReason'));
		} catch (error) {
			console.error('Failed to save loss reason:', error);
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={loading ? undefined : handleClose} sx={{ minWidth: '25%' }}>
			<DialogTitle>Loss reason</DialogTitle>
			<DialogContent sx={{ paddingBottom: '1em', minWidth: 360 }}>
				<TextField
					autoFocus
					fullWidth
					multiline
					rows={3}
					value={lossReason}
					onChange={(e) => setLossReason(e.target.value)}
					inputProps={{ maxLength: MAX_LENGTH }}
					disabled={loading}
				/>
				<FormHelperText sx={{ textAlign: 'right' }}>
					{`${lossReason.length}/${MAX_LENGTH}`}
				</FormHelperText>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					variant='contained'
					disabled={loading || !lossReason.trim()}
					onClick={handleConfirm}
				>
					{loading ? (
						<Box sx={{ padding: '0 2em' }}>
							<CircularProgress size='1em' color='inherit' />
						</Box>
					) : (
						'Confirm'
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
